import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, numberScheme, stringScheme, urlScheme } from 'src/utils/validation-schemes';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
} from 'src/services/knowledgeBaseService';
import {
  WEBSITE_INTERVAL,
  WEBSITE_INITIAL_REQUEST,
  WEBSITE_INITIAL_REQUEST_V2,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { useFieldArray } from 'react-hook-form';
import { CloseIcon } from 'src/assets/icons/CloseIcon';
import { API_VERSION } from 'src/constants/api-routes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { replaceSpecialSymbols } from 'src/helpers/replaceSpecialSymbols';
import { generateUniqueName } from 'src/utils/fileUtils';

export const CreateWebsitePopup = ({
  knowledgeBaseId = null,
  trigger,
  closeSourceModal = () => {},
  disabled = false,
  initialRequest = {},
}) => {
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV1 = version === API_VERSION.V1;
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation() as any;
  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      urls: [{ value: '' }],
      exclude_urls: [],
      name: '',
      autoSync: !isTrialPlan,
      recursive: true,
      interval: isV1 ? WEBSITE_INTERVAL.MONTHLY : WEBSITE_INTERVAL.WEEKLY,
    },
    resolver: yupResolver(
      object({
        name: stringScheme({ required: false }).label('Name'),
        urls: array()
          .of(
            object({
              value: urlScheme({ required: true }).label('URL'),
            }),
          )
          .min(1, 'At least one URL is required'),
        exclude_urls: array().of(
          object({
            value: urlScheme({ required: false }).label('Exclude URL'),
          }),
        ),
        recursive: booleanScheme({ required: false }).label('Sync Nested Pages'),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const {
    fields,
    append,
    remove,
    replace: replaceFields,
  } = useFieldArray({
    control,
    name: 'urls',
    rules: { minLength: 1 },
  });

  const {
    fields: excludeFields,
    append: appendExclude,
    remove: removeExclude,
    replace: replaceExclude,
  } = useFieldArray({
    control,
    name: 'exclude_urls',
  });

  useEffect(() => {
    if (isTrialPlan) {
      reset({
        autoSync: !isTrialPlan,
        recursive: true,
        interval: WEBSITE_INTERVAL.MONTHLY,
        urls: [{ value: '' }],
      });
      replaceFields([{ value: '' }]);
      replaceExclude([]);
    }
  }, [isTrialPlan]);

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);
      closeSourceModal();
      close();
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isLoadingCreateCollector || isLoadingCollectors) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateCollector, isLoadingCollectors]);

  const submit = (data) => {
    const name = replaceSpecialSymbols(data.urls[0]?.value.trim()) || WEBSITE_INITIAL_REQUEST.name;
    let body;

    if (isV1) {
      body = { ...WEBSITE_INITIAL_REQUEST, ...initialRequest };
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.urls = data.urls.map((url) => url.value.trim());
    } else {
      body = { ...WEBSITE_INITIAL_REQUEST_V2, ...initialRequest };
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.url = data.urls[0].value.trim();
    }

    if (data?.exclude_urls?.length) {
      body.exclude_urls = data.exclude_urls.map((url) => url.value.trim());
    }

    body.recursive = data.recursive;
    body.name = generateUniqueName(name, collectorsResponse);

    createCollector({ id: knowledgeBaseId || id, body });
  };

  const close = () => {
    replaceFields([{ value: '' }]);
    replaceExclude([]);
    isTrialPlan
      ? reset({
          autoSync: !isTrialPlan,
          recursive: true,
          interval: WEBSITE_INTERVAL.MONTHLY,
          urls: [{ value: '' }],
        })
      : reset();
    setOpen(false);
  };

  const onOpenChange = (isOpen) => {
    if (!isOpen) {
      return close();
    }

    setOpen(isOpen);
  };

  const isAutoSync = watch('autoSync');
  const recursiveWatch = watch('recursive');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger
          disabled={disabled}
          asChild
          className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
        >
          {trigger}
        </DialogTrigger>
        <DialogContent closeButtonHidden className="pb-0">
          <DialogHeader>
            <DialogTitle>Add website</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 pt-4">
            <div className="content">
              <form
                id="create-new-website-form"
                onSubmit={handleSubmit(submit)}
                className={'flex flex-col gap-5'}
              >
                <div className="flex flex-col gap-3">
                  {fields.map((field, index) => (
                    <div key={field.id} className="relative">
                      <Controller
                        control={control}
                        name={`urls.${index}.value`}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            type="text"
                            label={'URL'}
                            placeholder="https://cosupport.ai"
                            invalidMessage={error?.message}
                            value={value}
                            isValid={!error?.message}
                            onChange={onChange}
                          />
                        )}
                      />
                      {fields.length > 1 && (
                        <button className="absolute top-1.5 right-0" onClick={() => remove(index)}>
                          <CloseIcon />
                        </button>
                      )}
                    </div>
                  ))}
                  {isV1 && (
                    <button
                      type="button"
                      onClick={() => append({ value: '' })}
                      className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
                    >
                      Add URL
                    </button>
                  )}
                </div>
                <div className={'flex flex-row gap-5'}>
                  <div className={'input-container'}>
                    <Controller
                      control={control}
                      name="recursive"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <span className="flex flex-row items-center gap-4">
                          <span className="flex items-center gap-4">
                            <Switch type="black" checked={value} onCheckedChange={onChange} />
                          </span>
                          <span className={'flex flex-row items-center gap-2.5'}>
                            <span
                              className={'text-ebony_opacity_30 font-medium font-jamjuree text-sm'}
                            >
                              Sync Nested Pages
                            </span>
                            <InputInfoToolTip
                              contentClassName={'max-w-[300px]'}
                              infoToolTip={{
                                side: 'right',
                                description: (
                                  <>
                                    When enabled, the scraper will capture the main URL and all its
                                    subpages. If disabled, only the main URL will be synced.
                                  </>
                                ),
                              }}
                            />
                          </span>
                        </span>
                      )}
                    />
                  </div>
                </div>
                {recursiveWatch && (
                  <div className="flex flex-col gap-3">
                    {excludeFields.map((field, index) => (
                      <div key={field.id} className="relative">
                        <Controller
                          control={control}
                          name={`exclude_urls.${index}.value`}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                              type="text"
                              placeholder="https://www"
                              label={'Excluded URL'}
                              invalidMessage={error?.message}
                              value={value}
                              isValid={!error?.message}
                              onChange={onChange}
                            />
                          )}
                        />
                        <button
                          className="absolute top-1.5 right-0"
                          onClick={() => removeExclude(index)}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => appendExclude({ value: '' })}
                      className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
                    >
                      Exclude URL
                    </button>
                  </div>
                )}
                <div className={'flex flex-row gap-5'}>
                  <div className={'input-container'}>
                    <Controller
                      control={control}
                      name="autoSync"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <span className="flex flex-row gap-4">
                          <span className="flex items-center gap-4">
                            <Switch
                              type="black"
                              checked={value}
                              onCheckedChange={onChange}
                              disabled={isTrialPlan}
                            />
                          </span>
                          <span
                            className={
                              'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                            }
                          >
                            Auto-sync
                            {isTrialPlan && (
                              <InputInfoToolTip
                                infoToolTip={{
                                  side: 'right',
                                  description: <>Unavailable for trial plan</>,
                                }}
                              />
                            )}
                          </span>
                        </span>
                      )}
                    />
                  </div>
                  {isAutoSync && (
                    <div className={'input-container'}>
                      <Controller
                        control={control}
                        name="interval"
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <RadioGroup
                            onChange={onChange}
                            onValueChange={onChange}
                            defaultValue={value as any}
                            disabled={isTrialPlan || !isV1}
                            className="flex flex-row gap-5 items-center"
                          >
                            <div className={clsx('flex items-center space-x-[7px] space-y-0')}>
                              <RadioGroupItem
                                value={WEBSITE_INTERVAL.WEEKLY as any}
                                id={'interval-weekly'}
                              />
                              <label
                                htmlFor={'interval-weekly'}
                                className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                              >
                                <p
                                  className={clsx(
                                    'font-medium text-sm text-ebony opacity-50 transition',
                                    !(isTrialPlan || !isV1) && 'hover:opacity-100',
                                  )}
                                >
                                  Weekly
                                </p>
                              </label>
                            </div>
                            <div className="flex items-center space-x-[7px] space-y-0">
                              <RadioGroupItem
                                value={WEBSITE_INTERVAL.MONTHLY as any}
                                id={'interval-monthly'}
                              />
                              <label
                                htmlFor={'interval-monthly'}
                                className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                              >
                                <p
                                  className={clsx(
                                    'font-medium text-sm text-ebony opacity-50 transition',
                                    !(isTrialPlan || !isV1) && 'hover:opacity-100',
                                  )}
                                >
                                  Monthly
                                </p>
                              </label>
                            </div>
                          </RadioGroup>
                        )}
                      />
                    </div>
                  )}
                </div>
                <div className="sticky bottom-0 w-full bg-white">
                  <div className="flex justify-center my-5">
                    <div className="flex gap-2.5">
                      <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                        Cancel
                      </Button>
                      <Button disabled={!isDirty} type="submit" className="min-w-24">
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
